/**
 * Typed fetchers for /api/v1/indexes — document indexes, which the UI calls
 * **collections**. The API, code and database keep `document_index`; only
 * user-facing labels say "collection".
 *
 * Types come from the OpenAPI-generated schema. Regenerate via `pnpm gen:api`
 * after a backend Pydantic change.
 */

import type { components } from "./_generated/schema";
import { ApiError, apiFetch, type ApiErrorBody } from "./apiFetch";

export type IndexOut = components["schemas"]["IndexOut"];
export type IndexListOut = components["schemas"]["IndexListOut"];
export type IndexDetailOut = components["schemas"]["IndexDetailOut"];
export type IndexedDocumentOut = components["schemas"]["IndexedDocumentOut"];
export type CreateIndexIn = components["schemas"]["CreateIndexIn"];
export type AddBlobDocumentsIn = components["schemas"]["AddBlobDocumentsIn"];
export type AddVeevaDocumentsIn = components["schemas"]["AddVeevaDocumentsIn"];
export type VeevaDocumentRefIn = components["schemas"]["VeevaDocumentRefIn"];
export type AddDocumentsOut = components["schemas"]["AddDocumentsOut"];
export type RemoveDocumentsOut = components["schemas"]["RemoveDocumentsOut"];

/** The resource type sharing uses for a collection (`POST /shares`). */
export const DOCUMENT_INDEX_RESOURCE = "document_index";

const base = (id: string) => `/api/v1/indexes/${encodeURIComponent(id)}`;

/** Grant-scoped: the caller's own collections plus those shared with them. */
export function listIndexes(token: string): Promise<IndexListOut> {
  return apiFetch<IndexListOut>("/api/v1/indexes", { token });
}

/** `background` marks a polling read (ADR-0046): served, but not user activity. */
export function getIndex(
  token: string,
  indexId: string,
  opts: { background?: boolean } = {},
): Promise<IndexDetailOut> {
  return apiFetch<IndexDetailOut>(base(indexId), { token, background: opts.background });
}

/** `409 document_index_exists` on a duplicate name. */
export function createIndex(token: string, input: CreateIndexIn): Promise<IndexOut> {
  return apiFetch<IndexOut>("/api/v1/indexes", { method: "POST", token, json: input });
}

export function updateIndex(
  token: string,
  indexId: string,
  input: { name: string; description: string | null; reason?: string },
): Promise<IndexOut> {
  return apiFetch<IndexOut>(base(indexId), { method: "PATCH", token, json: input });
}

/** Add Blob or Veeva documents. Metadata only — nothing is read until a build. */
export function addDocuments(
  token: string,
  indexId: string,
  input: AddBlobDocumentsIn | AddVeevaDocumentsIn,
): Promise<AddDocumentsOut> {
  return apiFetch<AddDocumentsOut>(`${base(indexId)}/documents`, {
    method: "POST",
    token,
    json: input,
  });
}

export function removeDocuments(
  token: string,
  indexId: string,
  documentIds: string[],
  reason: string,
): Promise<RemoveDocumentsOut> {
  return apiFetch<RemoveDocumentsOut>(`${base(indexId)}/documents`, {
    method: "DELETE",
    token,
    json: { document_ids: documentIds, reason },
  });
}

/** `202` with status `building`. `409 document_index_busy` if one is running;
 *  `409 document_index_build_unavailable` when no embedding deployment is set. */
export function buildIndex(token: string, indexId: string): Promise<IndexOut> {
  return apiFetch<IndexOut>(`${base(indexId)}/build`, { method: "POST", token });
}

/** Deletes the collection and the platform's own copies. Source files in Blob
 *  and Veeva are never touched. */
export function deleteIndex(token: string, indexId: string, reason: string): Promise<void> {
  return apiFetch<void>(`${base(indexId)}?reason=${encodeURIComponent(reason)}`, {
    method: "DELETE",
    token,
  });
}

/** Streams the original document (audited server-side). Use with `downloadFile`. */
export function indexDocumentContentUrl(indexId: string, documentId: string): string {
  return `${base(indexId)}/documents/${encodeURIComponent(documentId)}/content`;
}

/**
 * Upload one local file with progress. `fetch` cannot report upload progress,
 * so this uses XMLHttpRequest — one request per file, which is also what makes
 * a per-file progress bar and a per-file error possible. Errors are mapped to
 * the same `ApiError` shape `apiFetch` throws.
 */
export function uploadIndexFile(
  token: string,
  indexId: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<AddDocumentsOut> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${base(indexId)}/uploads`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, "network_error", "Network error", null));
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = xhr.responseText;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve(body as AddDocumentsOut);
        return;
      }
      const envelope = (body && typeof body === "object" ? body : null) as ApiErrorBody | null;
      reject(
        new ApiError(
          xhr.status,
          envelope?.error?.code ?? `http_${xhr.status}`,
          envelope?.error?.message ?? `HTTP ${xhr.status}`,
          envelope ?? (typeof body === "string" ? body : null),
        ),
      );
    };
    const form = new FormData();
    form.append("files", file);
    xhr.send(form);
  });
}
